/// <amd-module name="Artifacts/Connect/tools/nuget/Nuget" />
import { CopyableCode, DetailsCard, PublishDetailsCard } from "Artifacts/Connect/components/Common";
import { OperatingSystem } from "Artifacts/Connect/Constants";
import * as Res from "Artifacts/Connect/Resources";
import { PackagingService, TfsService } from "Artifacts/Connect/services/PackagingService";
import { ToolService } from "Artifacts/Connect/services/ToolService";
import { IToolConnectProps } from "Artifacts/Connect/tools/IToolConnectProps";
import { NugetProtocol } from "Artifacts/Connect/tools/nuget/Protocol";
import { ICombinedFeedView } from "Artifacts/Connect/types/ICombinedFeedView";
import { ITool, ToolId } from "Artifacts/Connect/types/ITool";
import { getIsHosted } from "Artifacts/Root/helpers/CollectionHelpers";
import { getIsUserMember } from "Artifacts/Root/helpers/UserHelpers";
import * as React from "react";
import { useMemo } from "react";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPageData } from "VSS/Platform/Page";
import { Link } from "VSSUI/Link";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";

const NugetConnect: React.FC<IToolConnectProps<NugetProtocol>> = ({
  pageContext,
  protocol,
  feed: feed,
  packagingService,
  tfsService,
}) => {
  const source = feed.name;
  const url = protocol.getFeedUrl(packagingService, feed);
  const [selectedTabId, setSelectedTabId] = React.useState<string>(OperatingSystem.Windows);
  const tfsServiceUrl = tfsService.getSessionTokenUri();

  const { userIsMember, isHosted } = useMemo(() => {
    return {
      userIsMember: getIsUserMember(pageContext),
      isHosted: getIsHosted(pageContext),
    };
  }, [pageContext]);

  if (userIsMember) {
    return (
      <React.Fragment>
        <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
          {isHosted ? (
            <React.Fragment>
              <FormatComponent format={Res.ProjectSetup_CredProvider}>
                <b>{Res.CredentialProvider}</b>
                <b>{Res.CredentialProvider}</b>
              </FormatComponent>
              {protocol.getNuGetProjectSetUp(source, url, "")}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <TabBar
                onSelectedTabChanged={setSelectedTabId}
                selectedTabId={selectedTabId}
                tabSize={TabSize.Compact}
              >
                <Tab
                  name="Windows"
                  id={OperatingSystem.Windows}
                  iconProps={{ iconName: "WindowsLogo" }}
                />
                <Tab name={Res.Other} id={OperatingSystem.Other} iconProps={{ iconName: "PC1" }} />
              </TabBar>
              {protocol.getNuGetProjectSetUp(source, url, "tooldetails-smaller-margin")}
            </React.Fragment>
          )}

          {selectedTabId === OperatingSystem.Windows && !isHosted && (
            <React.Fragment>
              <p className="body-s">
                <FormatComponent format={Res.PAT_Issues}>
                  <Link className="body-s" onClick={() => setSelectedTabId(OperatingSystem.Other)}>
                    {Res.PAT_Instructions}
                  </Link>
                </FormatComponent>
              </p>
            </React.Fragment>
          )}

          {selectedTabId === OperatingSystem.Other && (
            <React.Fragment>
              <div className="title-s margin-top-16">{Res.SetupCredentials}</div>
              <div className="rhythm-vertical-20 card-steps margin-top-8">
                <div>
                  <div className="title-xs">{Res.Step} 1</div>
                  <FormatComponent format={Res.GeneratePAT}>
                    <Link href={tfsServiceUrl} target="_blank">
                      {Res.PersonalAccessToken}
                    </Link>
                    <em>{Res.Packaging}</em>
                  </FormatComponent>
                </div>

                <div>
                  <div className="title-xs">{Res.Step} 2</div>
                  <FormatComponent format={Res.PastePATAndUserName_DotNet_Nuget}>
                    <code>[{Res.PersonalAccessToken}]</code>
                  </FormatComponent>
                  <CopyableCode ariaLabel={Res.UpdateSource}>
                    nuget sources update -ValidAuthenticationTypes basic -Name {source} -UserName "
                    {source}" -Password [{Res.PersonalAccessToken}]
                  </CopyableCode>
                </div>
              </div>
            </React.Fragment>
          )}
        </DetailsCard>

        <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
          <p>{Res.RunCommand_Directory}</p>
          <CopyableCode ariaLabel={Res.RestorePackages}>nuget.exe restore</CopyableCode>
        </DetailsCard>

        <PublishDetailsCard feed={feed}>
          <p>{Res.PublishDetails_Nuget}</p>
          <CopyableCode ariaLabel={Res.PublishPackages}>
            nuget.exe push -Source "{source}" -ApiKey az &lt;packagePath&gt;
          </CopyableCode>
        </PublishDetailsCard>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
          <p>
            <FormatComponent format={Res.ProjectSetup_Nuget_Details}>
              <code>nuget.config</code>
              <code>.csproj</code>
              <code>.sln</code>
            </FormatComponent>
          </p>
          <CopyableCode
            text={protocol.nugetPackageSource(source, url)}
            ariaLabel={Res.ProjectSetup}
          >
            {protocol.nugetConfig(source, url)}
          </CopyableCode>
        </DetailsCard>

        <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
          <p>{Res.RunCommand_Directory}</p>
          <CopyableCode ariaLabel={Res.RestorePackages}>nuget.exe restore</CopyableCode>
        </DetailsCard>
      </React.Fragment>
    );
  }
};

class NugetTool implements ITool {
  readonly id = ToolId.NuGet;
  readonly name = "NuGet.exe";
  readonly protocol = new NugetProtocol();
  readonly allowNonMembers = true;
  readonly imagePath = `ms.azure-artifacts/connect/img/nuget.svg`;
  readonly toolLinkId = 2103785;
  readonly Component: React.ComponentType = NugetConnect;

  isEnvironmentSupported(pageData: IPageData): boolean {
    return true;
  }

  public isProtocolEnabled(pageContext: IVssPageContext): boolean {
    return true;
  }

  supportsGet(): boolean {
    return true;
  }

  supportsPush(feed: ICombinedFeedView): boolean {
    return feed.canPublishPackages;
  }

  renderComponent(
    pageContext: IVssPageContext,
    feed: ICombinedFeedView,
    packagingService: PackagingService,
    tfsService: TfsService,
  ): JSX.Element {
    return (
      <NugetConnect
        pageContext={pageContext}
        protocol={this.protocol}
        feed={feed}
        packagingService={packagingService}
        tfsService={tfsService}
      />
    );
  }

  public getToolLink = () => ({
    href: `https://go.microsoft.com/fwlink/?linkid=${this.toolLinkId}`,
    children: Res.CommandLineReference,
  });
}

ToolService.registerTool(new NugetTool());
